import { ref, onMounted } from 'vue'
import { NOTIFICATION_SOUND_BASE64 } from '@/assets/notificationSound'

export interface NotificationOptions {
  title: string
  body: string
  icon?: string
  tag?: string
  requireInteraction?: boolean
  onClick?: () => void
}

// Shared: the browser allowed audio after a click (see unlockAudio).
// A plain module-level ref, not Nuxt's useState: the widget bundle also uses
// this composable and has no Nuxt runtime. Only ever set in the browser.
const soundUnlocked = ref(false)

export function useNotifications() {
  const permission = ref<NotificationPermission>('default')
  const isSupported = ref(false)
  const soundEnabled = ref(true) // Can be controlled by user preferences

  // Audio instance for message notifications
  let messageAudio: HTMLAudioElement | null = null

  // Initialize immediately, not in onMounted
  const initializeAudio = () => {
    if (!messageAudio) {
      try {
        messageAudio = new Audio()

        // Use embedded base64 sound - works everywhere (widget and main app)
        messageAudio.src = NOTIFICATION_SOUND_BASE64
        messageAudio.volume = 1.0
        messageAudio.preload = 'auto'

        console.log('[useNotifications] Audio initialized with embedded base64 sound')
      } catch (error) {
        console.error('[useNotifications] Failed to initialize audio:', error)
      }
    }
    return messageAudio
  }

  onMounted(() => {
    isSupported.value = 'Notification' in window
    if (isSupported.value) {
      permission.value = Notification.permission
    }

    // Initialize audio element
    initializeAudio()
  })

  /**
   * Plays a short ping sound for message notifications
   */
  function playMessageSound(): void {
    if (!soundEnabled.value) {
      console.log('[useNotifications] Sound disabled, not playing')
      return
    }

    // Ensure audio is initialized
    const audio = initializeAudio()
    if (!audio) {
      console.warn('[useNotifications] Audio not available')
      return
    }

    try {
      // Reset audio to beginning if it's already playing
      audio.currentTime = 0
      console.log('[useNotifications] Playing embedded sound')
      // Play the sound
      audio.play().catch((error) => {
        // Ignore autoplay policy errors - sound will play on next user interaction
        if (error.name !== 'NotAllowedError') {
          console.warn('[useNotifications] Failed to play notification sound:', error)
        } else {
          console.log('[useNotifications] Autoplay blocked - user interaction required')
        }
      })
    } catch (error) {
      console.warn('[useNotifications] Error playing notification sound:', error)
    }
  }

  /**
   * Browsers block audio until the user interacts with the page. Call from a
   * click handler: plays the sound silently once so later pings are allowed.
   */
  async function unlockAudio(): Promise<boolean> {
    const audio = initializeAudio()
    if (!audio) return false
    try {
      audio.muted = true
      await audio.play()
      audio.pause()
      audio.currentTime = 0
      soundUnlocked.value = true
      return true
    } catch {
      // Still locked - the next click anywhere on the page will retry via playMessageSound
      return false
    } finally {
      audio.muted = false
    }
  }

  /**
   * Toggles sound notifications on/off
   */
  function toggleSound(enabled?: boolean): void {
    soundEnabled.value = enabled ?? !soundEnabled.value
  }

  async function requestPermission(): Promise<NotificationPermission> {
    if (!isSupported.value) {
      return 'denied'
    }

    const result = await Notification.requestPermission()
    permission.value = result
    return result
  }

  async function showNotification(options: NotificationOptions): Promise<Notification | null> {
    if (!isSupported.value) {
      console.warn('Notifications are not supported in this browser')
      return null
    }

    // Live value: the agent may have changed it in the browser's site settings
    permission.value = Notification.permission
    if (permission.value === 'default') {
      await requestPermission()
    }

    if (permission.value !== 'granted') {
      console.warn('Notification permission not granted')
      return null
    }

    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon ?? '/favicon.ico',
      tag: options.tag,
      requireInteraction: options.requireInteraction ?? false
    })

    if (options.onClick) {
      notification.onclick = () => {
        options.onClick!()
        notification.close()
      }
    }

    return notification
  }

  function notifyChatOpened(clientName: string, productNames: string[]) {
    const products = productNames.join(', ')
    showNotification({
      title: 'New Support Chat Opened',
      body: `${clientName} needs help with: ${products}`,
      tag: 'chat-opened',
      requireInteraction: true
    })
  }

  function notifyChatClosed(clientName: string) {
    showNotification({
      title: 'Support Chat Closed',
      body: `Chat with ${clientName} has been closed`,
      tag: 'chat-closed'
    })
  }

  function notifyAgentJoined(agentName: string) {
    showNotification({
      title: 'Agent Joined Chat',
      body: `${agentName} has joined your support chat`,
      tag: 'agent-joined'
    })
  }

  return {
    permission,
    isSupported,
    soundEnabled,
    soundUnlocked,
    requestPermission,
    showNotification,
    notifyChatOpened,
    notifyChatClosed,
    notifyAgentJoined,
    playMessageSound,
    unlockAudio,
    toggleSound
  }
}
